import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { updateService } from '../api/serviceApi';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';

/* ─── GOOGLE FONTS ────────────────────────────────────────────────── */
const FontLoader = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');`}</style>
);

/* ─── TOKENS ──────────────────────────────────────────────────────── */
const T = {
  primary: "#0B6E4F",
  accent: "#2BB673",
  accentSoft: "#E6F7EF",
  bg: "#F9FAF7",
  surface: "#FFFFFF",
  text: "#1F2933",
  muted: "#6B7280",
  border: "#E4EBE7",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  shadowMd: "0 8px 32px rgba(11,110,79,0.10)",
  shadowLg: "0 20px 60px rgba(0,0,0,0.13)",
};

/* ─── SERVICE SCHEMA ──────────────────────────────────────────────── */
const SCHEMA = {
  cleaning: {
    label: "Home Cleaning",
    icon: "🏠",
    allowedPricingModels: ["job", "hour"],
    services: [
      {
        id: "deep-clean",
        name: "Deep Home Clean",
        image:
          "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&q=80",
        basePrice: 4500,
        marketAvg: { min: 3500, max: 6000 },
        tag: "Most booked in your area",
      },
      {
        id: "airbnb-turnover",
        name: "Airbnb Turnover Clean",
        image:
          "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&q=80",
        basePrice: 3000,
        marketAvg: { min: 2500, max: 4500 },
        tag: "High demand",
      },
      {
        id: "move-in-out",
        name: "Move In / Out Clean",
        image:
          "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&q=80",
        basePrice: 6000,
        marketAvg: { min: 5000, max: 8000 },
        tag: null,
      },
    ],
    addons: {
      "deep-clean": [
        { id: "oven", label: "Inside Oven Cleaning", price: 500 },
        { id: "fridge", label: "Fridge Deep Clean", price: 400 },
        { id: "balcony", label: "Balcony Wash", price: 300 },
        { id: "laundry", label: "Laundry + Folding", price: 600 },
      ],
      "airbnb-turnover": [
        { id: "linen", label: "Fresh Linen Change", price: 350 },
        { id: "restock", label: "Amenities Restock", price: 250 },
        { id: "photo", label: "Post-clean Photography", price: 500 },
      ],
      "move-in-out": [
        { id: "carpet", label: "Carpet Steam Clean", price: 800 },
        { id: "windows", label: "Window Washing", price: 600 },
        { id: "walls", label: "Wall Wipe-down", price: 400 },
      ],
    },
  },
  plumbing: {
    label: "Plumbing",
    icon: "🔧",
    allowedPricingModels: ["job", "hour"],
    services: [
      {
        id: "pipe-repair",
        name: "Pipe Repair",
        image:
          "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=200&q=80",
        basePrice: 2500,
        marketAvg: { min: 2000, max: 4000 },
        tag: "High demand",
      },
      {
        id: "sink-install",
        name: "Sink Installation",
        image:
          "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=200&q=80",
        basePrice: 3500,
        marketAvg: { min: 3000, max: 5000 },
        tag: null,
      },
      {
        id: "drain-clear",
        name: "Drain Clearing",
        image:
          "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=200&q=80",
        basePrice: 1800,
        marketAvg: { min: 1500, max: 3000 },
        tag: "Most booked in your area",
      },
    ],
    addons: {
      "pipe-repair": [
        { id: "inspect", label: "Full Pipe Inspection", price: 600 },
        { id: "seal", label: "Pipe Sealing", price: 300 },
      ],
      "sink-install": [
        { id: "dispose", label: "Old Sink Disposal", price: 500 },
        { id: "taps", label: "Tap Replacement", price: 800 },
      ],
      "drain-clear": [
        { id: "enzyme", label: "Enzyme Treatment", price: 350 },
        { id: "camera", label: "Camera Inspection", price: 700 },
      ],
    },
  },
  beauty: {
    label: "Beauty + Wellness",
    icon: "💆",
    allowedPricingModels: ["job", "hour"],
    services: [
      {
        id: "hair-styling",
        name: "Hair Styling (Home Visit)",
        image:
          "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=200&q=80",
        basePrice: 2000,
        marketAvg: { min: 1500, max: 3000 },
        tag: "High demand",
      },
      {
        id: "massage",
        name: "Full Body Massage",
        image:
          "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=200&q=80",
        basePrice: 3500,
        marketAvg: { min: 2500, max: 5000 },
        tag: "Most booked in your area",
      },
    ],
    addons: {
      "hair-styling": [
        { id: "treatment", label: "Deep Conditioning", price: 600 },
        { id: "blowdry", label: "Blowdry Finish", price: 400 },
      ],
      massage: [
        { id: "aroma", label: "Aromatherapy", price: 500 },
        { id: "hotstone", label: "Hot Stone Upgrade", price: 800 },
      ],
    },
  },
  electrical: {
    label: "Electrical",
    icon: "⚡",
    allowedPricingModels: ["job", "hour"],
    services: [
      {
        id: "socket-install",
        name: "Socket Installation",
        image:
          "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=200&q=80",
        basePrice: 1500,
        marketAvg: { min: 1200, max: 2500 },
        tag: null,
      },
      {
        id: "fan-install",
        name: "Ceiling Fan Install",
        image:
          "https://images.unsplash.com/photo-1635274605638-d44babc08a4f?w=200&q=80",
        basePrice: 2500,
        marketAvg: { min: 2000, max: 3500 },
        tag: "High demand",
      },
    ],
    addons: {
      "socket-install": [{ id: "surge", label: "Surge Protector", price: 700 }],
      "fan-install": [
        { id: "remote", label: "Remote Control Kit", price: 900 },
        { id: "dimmer", label: "Speed Dimmer", price: 600 },
      ],
    },
  },
};

const LOCATIONS = [
  "Kilimani",
  "Westlands",
  "Karen",
  "Lavington",
  "Parklands",
  "Hurlingham",
  "Kileleshwa",
  "South C",
  "Langata",
  "Runda",
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FEE = 0.15;

/* ─── HELPERS ─────────────────────────────────────────────────────── */
function buildDefault(svcId, catId) {
  const cat = SCHEMA[catId];
  const svc = cat.services.find((s) => s.id === svcId);
  const addons = {};
  (cat.addons[svcId] || []).forEach((a) => {
    addons[a.id] = { enabled: false, price: a.price };
  });

  return {
    id: svcId,
    categoryId: catId,
    name: svc.name,
    image: svc.image,
    price: svc.basePrice,
    priceModel: cat.allowedPricingModels[0],
    status: "active",
    rating: 5.0,
    reviews: 0,
    location: "Kilimani",
    addons,
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    timeStart: "08:00",
    timeEnd: "18:00",
    minHours: 2,
    pricePerUnit: 150,
  };
}

function calcEarnings(svc) {
  let base = Number(svc.price) || 0;
  if (svc.priceModel === "hour") base *= Number(svc.minHours) || 1;
  const addon = Object.values(svc.addons || {}).reduce(
    (sum, a) => sum + (a.enabled ? Number(a.price) : 0),
    0
  );
  const gross = base + addon;
  const fee = Math.round(gross * FEE);
  return { gross, fee, net: gross - fee };
}

function priceSignal(price, marketAvg) {
  if (!marketAvg || price === null || price === undefined || price === "") return null;
  const n = Number(price);
  if (Number.isNaN(n) || n === 0) return null;

  if (n < marketAvg.min) {
    return { label: "Lower than most providers", color: T.danger, bg: "#FEF2F2" };
  }
  if (n > marketAvg.max) {
    return { label: "Premium pricing", color: "#7C3AED", bg: "#F5F3FF" };
  }
  return { label: "Competitive pricing", color: T.success, bg: "#F0FDF4" };
}

function formatNumber(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-US").format(n);
}

function stripToDigits(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function normalizeDigits(value) {
  const digits = stripToDigits(value);
  if (!digits) return "";
  return String(Number(digits));
}

const INIT = [
  {
    ...buildDefault("deep-clean", "cleaning"),
    rating: 4.8,
    reviews: 127,
    status: "active",
    addons: {
      oven: { enabled: true, price: 500 },
      fridge: { enabled: false, price: 400 },
      balcony: { enabled: true, price: 300 },
      laundry: { enabled: false, price: 600 },
    },
  },
  {
    ...buildDefault("airbnb-turnover", "cleaning"),
    rating: 4.9,
    reviews: 64,
    status: "active",
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    addons: {
      linen: { enabled: true, price: 350 },
      restock: { enabled: true, price: 250 },
      photo: { enabled: false, price: 500 },
    },
  },
  {
    ...buildDefault("pipe-repair", "plumbing"),
    rating: 4.7,
    reviews: 23,
    status: "paused",
    priceModel: "hour",
    price: 1200,
    addons: {
      inspect: { enabled: false, price: 600 },
      seal: { enabled: true, price: 300 },
    },
  },
];

/* ─── ATOMS ───────────────────────────────────────────────────────── */
function Dot({ status }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11.5,
        fontWeight: 600,
        color: status === "active" ? T.success : T.muted,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: status === "active" ? T.success : "#CBD5E0",
          display: "inline-block",
          boxShadow:
            status === "active" ? `0 0 0 3px ${T.success}28` : "none",
          transition: "all 250ms",
        }}
      />
      {status === "active" ? "Active" : "Paused"}
    </span>
  );
}

function Toggle({ checked, onChange, size = "md" }) {
  const { isMobile } = useBreakpoint();
  const W = size === "sm" ? 32 : 42;
  const H = size === "sm" ? 18 : 24;
  const K = size === "sm" ? 14 : 20;
  const pad = (H - K) / 2;

  return (
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: W,
        height: H,
        borderRadius: H,
        background: checked ? T.accent : "#D1D5DB",
        border: "none",
        cursor: "pointer",
        position: "relative",
        transition: "background 220ms",
        flexShrink: 0,
        padding: 0,
        outline: "none",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: pad,
          left: checked ? W - K - pad : pad,
          width: K,
          height: K,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 220ms cubic-bezier(0.34,1.56,0.64,1)",
          boxShadow: "0 1px 5px rgba(0,0,0,0.22)",
        }}
      />
    </button>
  );
}

function Tabs({ options, value, onChange }) {
  const { isMobile } = useBreakpoint();
  return (
    <div
      style={{
        display: "flex",
        background: "#EFF3F1",
        borderRadius: 12,
        padding: 4,
        gap: 3,
      }}
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              padding: isMobile ? "6px 0" : "8px 0",
              borderRadius: 9,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: isMobile ? 12 : 13,
              fontWeight: active ? 700 : 500,
              transition: "all 200ms",
              background: active ? T.surface : "transparent",
              color: active ? T.primary : T.muted,
              boxShadow: active ? "0 1px 6px rgba(0,0,0,0.09)" : "none",
              minHeight: isMobile ? tokens.spacing[44] : "auto",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 12,
        fontWeight: 600,
        color: T.muted,
        marginBottom: 6,
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </label>
  );
}

const NumericInput = forwardRef(function NumericInput(
  {
    label,
    value,
    onChange,
    prefix,
    suffix,
    hint,
    disabled,
    compact = false,
    wrapperStyle,
    inputStyle,
    placeholder = "Enter amount",
    autoClearZeroOnFocus = true,
    onFocus,
    onBlur,
  },
  ref
) {
  const { isMobile } = useBreakpoint();
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(
    value === null || value === undefined ? "" : formatNumber(value)
  );

  useEffect(() => {
    if (!focused) {
      setText(value === null || value === undefined ? "" : formatNumber(value));
    }
  }, [value, focused]);

  function commit(nextText) {
    const clean = normalizeDigits(nextText);
    if (clean === "") {
      setText("");
      onChange(0);
      return;
    }

    const pretty = formatNumber(clean);
    setText(pretty);
    onChange(Number(clean));
  }

  function handleFocus(e) {
    setFocused(true);
    if (autoClearZeroOnFocus && Number(value) === 0) {
      setText("");
    }
    if (onFocus) onFocus(e);
  }

  function handleChange(e) {
    const raw = e.target.value;
    const clean = normalizeDigits(raw);
    if (clean === "") {
      setText("");
      onChange(0);
      return;
    }
    const pretty = formatNumber(clean);
    setText(pretty);
    onChange(Number(clean));
  }

  function handleBlur(e) {
    setFocused(false);
    const clean = normalizeDigits(text);
    if (clean === "") {
      setText("0");
      onChange(0);
    } else {
      const pretty = formatNumber(clean);
      setText(pretty);
      onChange(Number(clean));
    }
    if (onBlur) onBlur(e);
  }

  const shellStyle = {
    display: "flex",
    alignItems: "center",
    background: disabled ? "#F5F5F5" : T.bg,
    border: `1.5px solid ${focused ? T.accent : T.border}`,
    borderRadius: compact ? 10 : 12,
    overflow: "hidden",
    transition: "border-color 180ms, background 180ms, box-shadow 180ms",
    boxShadow: focused && !disabled ? "0 0 0 3px rgba(43,182,115,0.08)" : "none",
    opacity: disabled ? 0.72 : 1,
    ...wrapperStyle,
  };

  const baseInputStyle = {
    flex: 1,
    minWidth: 0,
    padding: compact ? (isMobile ? "4px 6px" : "6px 9px") : (isMobile ? "8px 10px" : "11px 14px"),
    border: "none",
    background: "transparent",
    fontSize: compact ? (isMobile ? 12 : 13) : (isMobile ? 13 : 14.5),
    fontWeight: 700,
    color: T.text,
    fontFamily: "inherit",
    outline: "none",
    textAlign: "right",
    letterSpacing: "0.01em",
    ...inputStyle,
  };

  const prefixStyle = {
    padding: compact ? (isMobile ? "0 6px" : "0 10px") : (isMobile ? "0 10px" : "0 13px"),
    fontSize: compact ? (isMobile ? 10 : 11.5) : (isMobile ? 11 : 12.5),
    fontWeight: 600,
    color: T.muted,
    borderRight: `1px solid ${T.border}`,
    display: "flex",
    alignItems: "center",
    minHeight: compact ? (isMobile ? 30 : 34) : (isMobile ? 38 : 44),
    flexShrink: 0,
  };

  const suffixStyle = {
    padding: compact ? (isMobile ? "0 6px" : "0 10px") : (isMobile ? "0 10px" : "0 13px"),
    fontSize: compact ? (isMobile ? 10 : 11.5) : (isMobile ? 11 : 12),
    color: T.muted,
    flexShrink: 0,
    whiteSpace: "nowrap",
  };

  return (
    <div style={wrapperStyle ? wrapperStyle : undefined}>
      <div style={label ? { marginBottom: 0 } : undefined}>
        {label && <FieldLabel>{label}</FieldLabel>}
      </div>

      <div style={shellStyle}>
        {prefix && <span style={prefixStyle}>{prefix}</span>}
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={text}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={focused ? placeholder : ""}
          style={baseInputStyle}
        />
        {suffix && <span style={suffixStyle}>{suffix}</span>}
      </div>

      {hint && (
        <p style={{ fontSize: 11, color: T.muted, marginTop: 5, fontStyle: "italic" }}>
          {hint}
        </p>
      )}
    </div>
  );
});

function KshInput({ label, value, onChange, suffix, hint, disabled }) {
  return (
    <NumericInput
      label={label}
      value={value}
      onChange={onChange}
      prefix="Ksh"
      suffix={suffix}
      hint={hint}
      disabled={disabled}
      placeholder="Enter amount"
    />
  );
}

function NumInput({ label, value, onChange, suffix }) {
  return (
    <NumericInput
      label={label}
      value={value}
      onChange={onChange}
      suffix={suffix}
      placeholder="Enter amount"
    />
  );
}

function TimeInput({ label, value, onChange }) {
  const [f, setF] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <div>
      {label && <FieldLabel>{label}</FieldLabel>}
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          width: "100%",
          padding: isMobile ? "8px 12px" : "11px 14px",
          border: `1.5px solid ${f ? T.accent : T.border}`,
          borderRadius: 12,
          background: T.bg,
          fontSize: isMobile ? 13 : 14,
          fontWeight: 600,
          color: T.text,
          fontFamily: "inherit",
          outline: "none",
          transition: "border-color 180ms, box-shadow 180ms",
          boxShadow: f ? "0 0 0 3px rgba(43,182,115,0.08)" : "none",
          minHeight: isMobile ? tokens.spacing[44] : "auto",
        }}
      />
    </div>
  );
}

function SecHead({ label, locked }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
      <span
        style={{
          fontSize: isMobile ? 9.5 : 10.5,
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: T.muted,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {locked && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            background: "#EFF3F1",
            color: T.muted,
            padding: "2px 8px",
            borderRadius: 5,
            flexShrink: 0,
          }}
        >
          Platform Defined
        </span>
      )}
      <div style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );
}

/* ─── EARNINGS CARD ───────────────────────────────────────────────── */
function EarningsCard({ svc }) {
  const { gross, fee, net } = calcEarnings(svc);
  const { isMobile } = useBreakpoint();

  return (
    <div
      style={{
        background: "linear-gradient(135deg,#F0FAF5,#E6F7EF)",
        borderRadius: 16,
        padding: isMobile ? "16px 18px" : "20px 22px",
        border: "1px solid #BFE5D2",
        marginTop: 14,
      }}
    >
      <p
        style={{
          fontSize: isMobile ? 9.5 : 10.5,
          fontWeight: 800,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: T.primary,
          marginBottom: 14,
        }}
      >
        Earnings Preview
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.muted }}>
          <span>Service total</span>
          <span style={{ fontWeight: 600, color: T.text }}>Ksh {gross.toLocaleString()}</span>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.muted }}>
          <span>Platform fee (15%)</span>
          <span style={{ fontWeight: 600, color: "#DC2626" }}>- Ksh {fee.toLocaleString()}</span>
        </div>

        <div style={{ height: 1, background: "#BFE5D2", margin: "3px 0" }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span
            style={{
              fontSize: isMobile ? 11 : 12,
              fontWeight: 800,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: T.primary,
            }}
          >
            You Earn
          </span>
          <span
            style={{
              fontSize: isMobile ? 24 : 28,
              fontWeight: 800,
              color: T.primary,
              letterSpacing: "-0.03em",
            }}
          >
            Ksh {net.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── ADDON ROW ───────────────────────────────────────────────────── */
function AddonRow({ addon, state, onChange }) {
  const [flash, setFlash] = useState(false);
  const priceRef = useRef(null);
  const { isMobile } = useBreakpoint();

  useEffect(() => {
    if (state.enabled) {
      const t = setTimeout(() => {
        priceRef.current?.focus();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [state.enabled]);

  function toggle(v) {
    onChange("enabled", v);
    if (v) {
      setFlash(true);
      setTimeout(() => setFlash(false), 500);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 8 : 12,
        padding: isMobile ? "8px 12px" : "12px 16px",
        borderRadius: 12,
        border: `1.5px solid ${state.enabled ? T.accent + "66" : T.border}`,
        background: flash ? "#D1FAE5" : state.enabled ? "#F6FBF8" : T.bg,
        transition: "all 220ms ease",
        flexWrap: isMobile ? "wrap" : "nowrap",
      }}
    >
      <Toggle checked={!!state.enabled} onChange={toggle} size="sm" />
      <span
        style={{
          flex: 1,
          fontSize: isMobile ? 12 : 13,
          fontWeight: state.enabled ? 600 : 400,
          color: state.enabled ? T.text : T.muted,
          transition: "all 200ms",
        }}
      >
        {addon.label}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 11.5, color: T.muted, fontWeight: 500 }}>Ksh</span>
        <NumericInput
          ref={priceRef}
          value={state.price}
          onChange={(v) => onChange("price", v)}
          disabled={!state.enabled}
          compact
          placeholder="Enter amount"
          wrapperStyle={{ width: isMobile ? 80 : 88 }}
          inputStyle={{
            textAlign: "right",
            padding: isMobile ? "4px 6px" : "6px 8px",
            fontSize: isMobile ? 12 : 13,
          }}
        />
      </div>
    </div>
  );
}

/* ─── ADD SERVICE MODAL ───────────────────────────────────────────── */
function AddModal({ onClose, onAdd }) {
  const { isMobile } = useBreakpoint();
  const [step, setStep] = useState(1);
  const [catId, setCatId] = useState(null);
  const [svcId, setSvcId] = useState(null);
  const cat = catId ? SCHEMA[catId] : null;
  const svcs = cat?.services || [];

  function load() {
    if (!svcId || !catId) return;
    onAdd(svcs.find((s) => s.id === svcId), catId);
    onClose();
  }

  const modalPadding = isMobile ? "20px 24px 24px" : "24px 32px 32px";
  const modalHeaderPadding = isMobile ? "20px 24px 16px" : "28px 32px 22px";
  const buttonPadding = isMobile ? "12px" : "15px";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,20,16,0.6)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "fadeIn 180ms",
      }}
    >
      <div
        style={{
          background: T.surface,
          borderRadius: 24,
          width: "100%",
          maxWidth: isMobile ? "95%" : 540,
          boxShadow: T.shadowLg,
          overflow: "hidden",
          animation: "fadeUp 280ms cubic-bezier(0.34,1.4,0.64,1)",
        }}
      >
        <div style={{ padding: modalHeaderPadding, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {[1, 2].map((n) => (
                  <div
                    key={n}
                    style={{
                      height: 3,
                      width: n === step ? 32 : 16,
                      borderRadius: 2,
                      background:
                        n <= step
                          ? `linear-gradient(90deg,${T.accent},${T.primary})`
                          : T.border,
                      transition: "all 300ms",
                    }}
                  />
                ))}
              </div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: T.accent,
                  marginBottom: 5,
                }}
              >
                Step {step} of 2
              </p>
              <h3
                style={{
                  fontSize: isMobile ? 18 : 21,
                  fontWeight: 700,
                  color: T.text,
                  letterSpacing: "-0.02em",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {step === 1 ? "Choose a category" : "Select a service"}
              </h3>
            </div>

            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: `1.5px solid ${T.border}`,
                background: "transparent",
                cursor: "pointer",
                fontSize: 18,
                color: T.muted,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: isMobile ? tokens.spacing[44] : "auto",
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: modalPadding }}>
          {step === 1 ? (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 11 }}>
              {Object.entries(SCHEMA).map(([id, c]) => (
                <button
                  key={id}
                  onClick={() => {
                    setCatId(id);
                    setStep(2);
                    setSvcId(null);
                  }}
                  style={{
                    padding: isMobile ? "14px" : "18px",
                    borderRadius: 14,
                    border: `1.5px solid ${T.border}`,
                    background: T.bg,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 180ms",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontFamily: "inherit",
                    minHeight: isMobile ? tokens.spacing[44] : "auto",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = T.accent;
                    e.currentTarget.style.background = T.accentSoft;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = T.border;
                    e.currentTarget.style.background = T.bg;
                  }}
                >
                  <span style={{ fontSize: 22 }}>{c.icon}</span>
                  <span style={{ fontSize: isMobile ? 12.5 : 13.5, fontWeight: 600, color: T.text }}>
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <>
              <button
                onClick={() => {
                  setStep(1);
                  setSvcId(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  color: T.muted,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  marginBottom: 18,
                  fontFamily: "inherit",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}
              >
                ← Back
              </button>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {svcs.map((s) => {
                  const a = svcId === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSvcId(s.id)}
                      style={{
                        padding: isMobile ? "12px" : "15px 16px",
                        borderRadius: 14,
                        border: `1.5px solid ${a ? T.accent : T.border}`,
                        background: a ? T.accentSoft : T.bg,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: isMobile ? 10 : 14,
                        transition: "all 180ms",
                        fontFamily: "inherit",
                        textAlign: "left",
                        minHeight: isMobile ? tokens.spacing[44] : "auto",
                      }}
                    >
                      <img
                        src={s.image}
                        alt={s.name}
                        style={{
                          width: isMobile ? 44 : 54,
                          height: isMobile ? 44 : 54,
                          borderRadius: 10,
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 3,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: T.text }}>
                            {s.name}
                          </span>
                          {s.tag && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                background: s.tag.includes("demand") ? "#FEF3C7" : T.accentSoft,
                                color: s.tag.includes("demand") ? "#92400E" : T.primary,
                                padding: "2px 7px",
                                borderRadius: 5,
                              }}
                            >
                              {s.tag}
                            </span>
                          )}
                        </div>
                        <p style={{ fontSize: isMobile ? 11 : 12, color: T.muted, margin: 0 }}>
                          Market avg: Ksh {s.marketAvg.min.toLocaleString()} to Ksh{" "}
                          {s.marketAvg.max.toLocaleString()}
                        </p>
                      </div>
                      {a && (
                        <span style={{ color: T.accent, fontSize: 16, flexShrink: 0 }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={load}
                disabled={!svcId}
                style={{
                  marginTop: 20,
                  width: "100%",
                  padding: buttonPadding,
                  borderRadius: 12,
                  border: "none",
                  cursor: svcId ? "pointer" : "not-allowed",
                  background: svcId
                    ? `linear-gradient(135deg,${T.accent},${T.primary})`
                    : "#E5E7EB",
                  color: svcId ? "#fff" : T.muted,
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  transition: "all 220ms",
                  boxShadow: svcId ? `0 6px 24px ${T.accent}44` : "none",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}
              >
                Load into Studio
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── SERVICE CARD ────────────────────────────────────────────────── */
function SvcCard({ svc, selected, onClick }) {
  const [h, setH] = useState(false);
  const { net } = calcEarnings(svc);
  const { isMobile } = useBreakpoint();

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: "100%",
        background: selected ? T.accentSoft : h ? "#FAFCFA" : T.surface,
        border: `1.5px solid ${selected ? T.accent : h ? "#C0D9CC" : T.border}`,
        borderRadius: 16,
        padding: isMobile ? "10px 12px" : "14px 16px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 10 : 14,
        transition: "all 200ms",
        boxShadow: selected ? `0 4px 20px ${T.accent}22` : h ? "0 4px 14px rgba(0,0,0,0.06)" : "none",
        transform: h && !selected ? "translateY(-2px)" : "none",
        textAlign: "left",
        fontFamily: "inherit",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}
    >
      <img
        src={svc.image}
        alt={svc.name}
        style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 11, objectFit: "cover", flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: isMobile ? 12.5 : 13.5,
            fontWeight: 700,
            color: T.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: 2,
          }}
        >
          {svc.name}
        </p>
        <p style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color: T.primary, marginBottom: 5 }}>
          Earn ~Ksh {net.toLocaleString()}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Dot status={svc.status} />
          <span style={{ fontSize: isMobile ? 10 : 11, color: T.muted }}>
            {svc.reviews > 0 ? `★ ${svc.rating} (${svc.reviews})` : "New listing"}
          </span>
        </div>
      </div>
      <span
        style={{
          color: selected ? T.accent : T.border,
          fontSize: isMobile ? 16 : 18,
          flexShrink: 0,
          transition: "color 200ms",
        }}
      >
        ›
      </span>
    </button>
  );
}

/* ─── CONFIG STUDIO ───────────────────────────────────────────────── */
function Studio({ svc, onChange, animKey }) {
  const { isMobile } = useBreakpoint();
  const cat = SCHEMA[svc.categoryId];
  const addons = cat?.addons[svc.id] || [];
  const marketAvg = cat?.services.find((s) => s.id === svc.id)?.marketAvg;
  const models = cat?.allowedPricingModels || ["job", "hour"];
  const activePrice = svc.priceModel === "unit" ? svc.pricePerUnit : svc.price;
  const signal = priceSignal(activePrice, marketAvg);

  function set(k, v) {
    onChange({ ...svc, [k]: v });
  }

  function toggleDay(d) {
    set("days", svc.days.includes(d) ? svc.days.filter((x) => x !== d) : [...svc.days, d]);
  }

  function setAddon(id, field, val) {
    onChange({
      ...svc,
      addons: {
        ...svc.addons,
        [id]: {
          ...svc.addons[id],
          [field]: val,
        },
      },
    });
  }

  const studioPadding = isMobile ? "24px 20px" : "32px 34px";
  const identityPadding = isMobile ? "14px 16px" : "20px 22px";
  const nameFontSize = isMobile ? 14.5 : 16.5;

  return (
    <div
      key={animKey}
      style={{
        animation: "slideIn 260ms cubic-bezier(0.34,1,0.64,1)",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 24 : 32,
      }}
    >
      {/* Identity */}
      <div
        style={{
          background: "#F6FBF8",
          borderRadius: 18,
          padding: identityPadding,
          border: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 16 }}>
          <img
            src={svc.image}
            alt={svc.name}
            style={{
              width: isMobile ? 48 : 62,
              height: isMobile ? 48 : 62,
              borderRadius: 13,
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <h3
                style={{
                  fontSize: nameFontSize,
                  fontWeight: 700,
                  color: T.text,
                  letterSpacing: "-0.02em",
                }}
              >
                {svc.name}
              </h3>
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  background: `${T.primary}16`,
                  color: T.primary,
                  padding: "2px 8px",
                  borderRadius: 5,
                  textTransform: "uppercase",
                }}
              >
                Verified
              </span>
            </div>
            <p style={{ fontSize: isMobile ? 11 : 12, color: T.muted, lineHeight: 1.5 }}>
              Defined by JiraniApp. Meets platform quality standards.
            </p>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 6,
              flexShrink: 0,
            }}
          >
            <Dot status={svc.status} />
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 11, color: T.muted }}>
                {svc.status === "active" ? "Pause" : "Activate"}
              </span>
              <Toggle
                checked={svc.status === "active"}
                onChange={(v) => set("status", v ? "active" : "paused")}
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Engine */}
      <div>
        <SecHead label="Pricing Engine" />
        <Tabs
          options={models.map((m) => ({
            value: m,
            label: m === "job" ? "Per Job" : m === "hour" ? "Per Hour" : "Per Unit",
          }))}
          value={svc.priceModel}
          onChange={(v) => set("priceModel", v)}
        />
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 13 }}>
          {svc.priceModel === "job" && (
            <KshInput
              label="Base Price"
              value={svc.price}
              onChange={(v) => set("price", v)}
              hint={
                marketAvg
                  ? `Market average: Ksh ${marketAvg.min.toLocaleString()} to Ksh ${marketAvg.max.toLocaleString()}`
                  : null
              }
            />
          )}

          {svc.priceModel === "hour" && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <KshInput
                label="Hourly Rate"
                value={svc.price}
                onChange={(v) => set("price", v)}
                suffix="/hr"
              />
              <NumInput
                label="Minimum Hours"
                value={svc.minHours ?? 2}
                onChange={(v) => set("minHours", v)}
                suffix="hrs"
              />
            </div>
          )}

          {svc.priceModel === "unit" && (
            <KshInput
              label="Price Per Unit"
              value={svc.pricePerUnit ?? 150}
              onChange={(v) => set("pricePerUnit", v)}
              suffix="/ sqm"
              hint="Set your rate per square metre"
            />
          )}

          {signal && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 14px",
                borderRadius: 9,
                background: signal.bg,
                border: `1px solid ${signal.color}22`,
                width: "fit-content",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: signal.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 600, color: signal.color }}>
                {signal.label}
              </span>
            </div>
          )}

          <EarningsCard svc={svc} />
        </div>
      </div>

      {/* Add-ons */}
      {addons.length > 0 && (
        <div>
          <SecHead label={`Add-ons for ${svc.name}`} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {addons.map((a) => (
              <AddonRow
                key={a.id}
                addon={a}
                state={svc.addons?.[a.id] || { enabled: false, price: a.price }}
                onChange={(field, val) => setAddon(a.id, field, val)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Availability */}
      <div>
        <SecHead label="Availability" />
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <FieldLabel>Service Location</FieldLabel>
            <div style={{ position: "relative" }}>
              <select
                value={svc.location}
                onChange={(e) => set("location", e.target.value)}
                style={{
                  width: "100%",
                  padding: isMobile ? "8px 12px 8px 12px" : "11px 40px 11px 14px",
                  borderRadius: 12,
                  border: `1.5px solid ${T.border}`,
                  background: T.bg,
                  fontSize: isMobile ? 12.5 : 13.5,
                  fontWeight: 600,
                  color: T.text,
                  fontFamily: "inherit",
                  outline: "none",
                  cursor: "pointer",
                  appearance: "none",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}
              >
                {LOCATIONS.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </select>
              <span
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: T.muted,
                  fontSize: 12,
                }}
              >
                ▾
              </span>
            </div>
          </div>

          <div>
            <FieldLabel>Working Days</FieldLabel>
            <div style={{ display: "flex", gap: isMobile ? 4 : 7, flexWrap: "wrap" }}>
              {DAYS.map((d) => {
                const on = svc.days.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggleDay(d)}
                    style={{
                      flex: isMobile ? "0 0 auto" : 1,
                      padding: isMobile ? "6px 0" : "9px 0",
                      minWidth: isMobile ? 36 : "auto",
                      borderRadius: 10,
                      border: `1.5px solid ${on ? T.accent : T.border}`,
                      background: on ? T.accentSoft : T.bg,
                      color: on ? T.primary : T.muted,
                      fontSize: isMobile ? 10 : 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 180ms",
                      fontFamily: "inherit",
                      minHeight: isMobile ? tokens.spacing[44] : "auto",
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            <TimeInput
              label="Start Time"
              value={svc.timeStart || "08:00"}
              onChange={(v) => set("timeStart", v)}
            />
            <TimeInput
              label="End Time"
              value={svc.timeEnd || "18:00"}
              onChange={(v) => set("timeEnd", v)}
            />
          </div>
        </div>
      </div>

      {/* Quality Standards */}
      <div style={{ marginBottom: 8 }}>
        <SecHead label="Quality Standards" locked />
        <div
          style={{
            borderRadius: 14,
            border: `1px solid ${T.border}`,
            overflow: "hidden",
            background: T.surface,
          }}
        >
          {[
            "Arrive with all required tools and supplies",
            "Complete the service within the agreed time window",
            "Deliver the defined service outcome to the client",
            "Maintain professional conduct throughout the visit",
          ].map((item, i, arr) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: isMobile ? "10px 14px" : "13px 17px",
                borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none",
              }}
            >
              <span style={{ color: T.accent, fontSize: 14, marginTop: 1, flexShrink: 0 }}>
                ✓
              </span>
              <span style={{ fontSize: isMobile ? 12 : 13, color: T.muted, lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>
        <button
          style={{
            marginTop: 11,
            fontSize: 12.5,
            color: T.primary,
            fontWeight: 600,
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            textDecoration: "underline",
            fontFamily: "inherit",
            textUnderlineOffset: 3,
            minHeight: isMobile ? tokens.spacing[44] : "auto",
          }}
        >
          View full service guidelines
        </button>
      </div>
    </div>
  );
}

/* ─── PAGE ────────────────────────────────────────────────────────── */
export default function ManageServicesPage() {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const [services, setServices] = useState(INIT);
  const [selId, setSelId] = useState(INIT[0].id);
  const [draft, setDraft] = useState(INIT[0]);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [animKey, setAnimKey] = useState(0);

  function select(id) {
    if (id === selId) return;
    setSelId(id);
    setDraft(services.find((s) => s.id === id));
    setDirty(false);
    setAnimKey((k) => k + 1);
  }

  async function save() {
    if (!dirty || saveState !== "idle") return;
    setSaveState("saving");

    try {
      await updateService(draft.id, {
        title:       draft.name,
        description: draft.bio || 'Professional service',
        price:       draft.price,
        address:     draft.location,
        pricingType: draft.priceModel === 'hour' ? 'hourly' : 'fixed',
      });

      setServices((p) => p.map((s) => (s.id === draft.id ? draft : s)));
      setSaveState("saved");
      setDirty(false);
      setToast("Your service is now live");
      setTimeout(() => {
        setSaveState("idle");
        setToast(null);
      }, 2500);
    } catch (err) {
      setServices((p) => p.map((s) => (s.id === draft.id ? draft : s)));
      setSaveState("saved");
      setDirty(false);
      setToast("Changes saved locally");
      setTimeout(() => {
        setSaveState("idle");
        setToast(null);
      }, 2500);
    }
  }

  function addSvc(svcDef, catId) {
    if (services.find((s) => s.id === svcDef.id)) {
      select(svcDef.id);
      return;
    }
    const n = { ...buildDefault(svcDef.id, catId), image: svcDef.image };
    setServices((p) => [...p, n]);
    setSelId(n.id);
    setDraft(n);
    setDirty(false);
    setAnimKey((k) => k + 1);
  }

  const handleDashboardClick = () => {
    navigate("/pro-dashboard");
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const active = services.filter((s) => s.status === "active").length;
  const paused = services.filter((s) => s.status === "paused").length;

  const leftPanelWidth = isMobile ? "100%" : (isTablet ? "42%" : "38%");
  const navPadding = isMobile ? "0 16px" : "0 32px";
  const bodyPadding = isMobile ? "20px 16px 40px" : "32px 44px 40px";
  const statsPadding = isMobile ? "0 16px 12px" : "0 26px 16px";
  const listPadding = isMobile ? "0 16px" : "0 26px";
  const addButtonPadding = isMobile ? "12px 16px 20px" : "16px 26px 22px";

  return (
    <>
      <FontLoader />
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(-14px) scale(0.95)}to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}}
        @keyframes checkPop{0%{transform:scale(0)}60%{transform:scale(1.3)}100%{transform:scale(1)}}
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:#C8D8D0;border-radius:2px}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
        input[type=number]{-moz-appearance:textfield}
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif",
          color: T.text,
        }}
      >
        {/* Toast */}
        {toast && (
          <div
            style={{
              position: "fixed",
              top: 28,
              left: "50%",
              transform: "translateX(-50%)",
              background: T.primary,
              color: "#fff",
              padding: isMobile ? "10px 20px" : "13px 26px",
              borderRadius: 50,
              fontSize: isMobile ? 12 : 13.5,
              fontWeight: 600,
              zIndex: 2000,
              boxShadow: `0 12px 40px ${T.primary}55`,
              animation: "toastIn 320ms cubic-bezier(0.34,1.4,0.64,1)",
              whiteSpace: isMobile ? "normal" : "nowrap",
              textAlign: "center",
              maxWidth: isMobile ? "90%" : "auto",
            }}
          >
            <span style={{ fontSize: 15 }}>✦</span> {toast}
          </div>
        )}

        {modal && <AddModal onClose={() => setModal(false)} onAdd={addSvc} />}

        {/* NAV */}
        <nav
          style={{
            height: isMobile ? 52 : 58,
            borderBottom: `1px solid ${T.border}`,
            background: T.surface,
            display: "flex",
            alignItems: "center",
            padding: navPadding,
            gap: isMobile ? 12 : 20,
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
          }}
        >
          <button
            onClick={handleDashboardClick}
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: isMobile ? 11.5 : 12.5,
              color: T.muted,
              fontWeight: 500,
              transition: "color 180ms",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              minHeight: isMobile ? tokens.spacing[44] : "auto",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.primary)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.muted)}
          >
            <span style={{ fontSize: 14 }}>←</span> Dashboard
          </button>

          <span style={{ width: 1, height: 18, background: T.border, flexShrink: 0 }} />
          <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: T.text }}>Manage Services</span>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
            <button
              onClick={handleLogoClick}
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "baseline",
                cursor: "pointer",
                transition: "opacity 180ms",
                background: "none",
                border: "none",
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.72")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 700,
                  fontSize: isMobile ? 15 : 17,
                  color: T.primary,
                  letterSpacing: "0.01em",
                }}
              >
                JiraniApp.
              </span>
            </button>
          </div>
        </nav>

        {/* BODY */}
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: isMobile ? "auto" : "calc(100vh - 58px - 70px)", overflow: isMobile ? "visible" : "hidden" }}>
          {/* LEFT */}
          <div
            style={{
              width: leftPanelWidth,
              minWidth: isMobile ? "100%" : 280,
              maxWidth: isMobile ? "100%" : 420,
              borderRight: isMobile ? "none" : `1px solid ${T.border}`,
              borderBottom: isMobile ? `1px solid ${T.border}` : "none",
              background: T.surface,
              display: "flex",
              flexDirection: "column",
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
            <div style={{ padding: isMobile ? "20px 16px 8px" : "28px 26px 14px" }}>
              <h1
                style={{
                  fontSize: isMobile ? 18 : 22,
                  fontWeight: 800,
                  color: T.text,
                  letterSpacing: "-0.03em",
                  marginBottom: 4,
                }}
              >
                My Services
              </h1>
              <p style={{ fontSize: isMobile ? 11.5 : 12.5, color: T.muted, lineHeight: 1.5 }}>
                Your services are how you earn on JiraniApp.
              </p>
            </div>

            <div style={{ padding: statsPadding, display: "flex", gap: 8 }}>
              {[
                { label: "Active", count: active, color: T.success, bg: "#F0FDF4" },
                { label: "Paused", count: paused, color: T.muted, bg: T.bg },
                { label: "Total", count: services.length, color: T.primary, bg: T.accentSoft },
              ].map((p) => (
                <div
                  key={p.label}
                  style={{
                    flex: 1,
                    background: p.bg,
                    borderRadius: 11,
                    padding: "9px 0",
                    textAlign: "center",
                  }}
                >
                  <p style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: p.color, lineHeight: 1.1 }}>
                    {p.count}
                  </p>
                  <p
                    style={{
                      fontSize: isMobile ? 9 : 10,
                      color: T.muted,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {p.label}
                  </p>
                </div>
              ))}
            </div>

            <div
              style={{
                flex: isMobile ? "none" : 1,
                overflowY: isMobile ? "visible" : "auto",
                padding: listPadding,
                display: "flex",
                flexDirection: "column",
                gap: 9,
                maxHeight: isMobile ? "none" : "calc(100vh - 58px - 70px - 140px)",
              }}
            >
              {services.map((s) => (
                <SvcCard
                  key={s.id}
                  svc={s}
                  selected={selId === s.id}
                  onClick={() => select(s.id)}
                />
              ))}
            </div>

            <div style={{ padding: addButtonPadding }}>
              <button
                onClick={() => setModal(true)}
                style={{
                  width: "100%",
                  padding: isMobile ? "11px" : "13px",
                  borderRadius: 13,
                  border: `1.5px dashed ${T.accent}`,
                  background: "transparent",
                  color: T.primary,
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "all 200ms",
                  fontFamily: "inherit",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = T.accentSoft;
                  e.currentTarget.style.borderStyle = "solid";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderStyle = "dashed";
                }}
              >
                <span style={{ fontSize: 19, fontWeight: 400, lineHeight: 1 }}>+</span> Add New Service
              </button>
            </div>
          </div>

          {/* RIGHT */}
          <div style={{ flex: 1, overflowY: isMobile ? "visible" : "auto", padding: bodyPadding }}>
            <div style={{ maxWidth: isMobile ? "100%" : 640, margin: "0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: isMobile ? 9.5 : 10.5,
                      fontWeight: 800,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: T.accent,
                      marginBottom: 5,
                    }}
                  >
                    Configuration Studio
                  </p>
                  <h2
                    style={{
                      fontSize: isMobile ? 17 : 19,
                      fontWeight: 800,
                      color: T.text,
                      letterSpacing: "-0.025em",
                    }}
                  >
                    {draft?.name}
                  </h2>
                </div>

                {dirty && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: isMobile ? 11 : 12,
                      fontWeight: 600,
                      color: "#92400E",
                      background: "#FEF3C7",
                      padding: "5px 12px",
                      borderRadius: 8,
                      animation: "fadeIn 200ms",
                      flexShrink: 0,
                    }}
                  >
                    <span>●</span> Unsaved changes
                  </div>
                )}
              </div>

              <div
                style={{
  background: T.surface,
  borderRadius: 20,
  boxShadow: T.shadowMd,
  padding: isMobile ? "24px 20px" : "32px 34px",
  border: `1px solid ${T.border}`,
}}
              >
                {draft && (
                  <Studio
                    svc={draft}
                    onChange={(v) => {
                      setDraft(v);
                      setDirty(true);
                    }}
                    animKey={animKey}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ACTION BAR */}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: isMobile ? 60 : 70,
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(14px)",
            borderTop: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "0 16px" : "0 44px",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
            zIndex: 200,
          }}
        >
          <p
            style={{
              fontSize: isMobile ? 11 : 13,
              color: dirty ? "#92400E" : T.muted,
              fontWeight: dirty ? 600 : 400,
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "all 250ms",
            }}
          >
            {dirty ? (
              <>
                <span style={{ color: T.warning }}>●</span> You have unsaved changes
              </>
            ) : (
              <>
                <span style={{ color: T.success }}>●</span> All changes saved
              </>
            )}
          </p>

          <div style={{ display: "flex", gap: isMobile ? 8 : 10 }}>
            <button
              onClick={() => {
                setDraft(services.find((s) => s.id === selId));
                setDirty(false);
                setAnimKey((k) => k + 1);
              }}
              disabled={!dirty}
              style={{
                padding: isMobile ? "6px 12px" : "10px 20px",
                borderRadius: 10,
                border: `1.5px solid ${dirty ? T.border : "transparent"}`,
                background: "transparent",
                color: dirty ? T.muted : "transparent",
                fontSize: isMobile ? 11 : 13,
                fontWeight: 600,
                cursor: dirty ? "pointer" : "default",
                fontFamily: "inherit",
                transition: "all 180ms",
                minHeight: isMobile ? tokens.spacing[44] : "auto",
              }}
            >
              Discard
            </button>

            <button
              onClick={save}
              disabled={!dirty || saveState !== "idle"}
              style={{
                padding: isMobile ? "6px 16px" : "10px 30px",
                borderRadius: 10,
                border: "none",
                cursor: dirty && saveState === "idle" ? "pointer" : "default",
                fontSize: isMobile ? 11 : 13,
                fontWeight: 700,
                fontFamily: "inherit",
                transition: "all 220ms",
                minWidth: isMobile ? 100 : 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: dirty
                  ? `linear-gradient(135deg,${T.accent},${T.primary})`
                  : "#E5E7EB",
                color: dirty ? "#fff" : T.muted,
                boxShadow:
                  dirty && saveState === "idle" ? `0 6px 22px ${T.accent}44` : "none",
                letterSpacing: "0.01em",
                minHeight: isMobile ? tokens.spacing[44] : "auto",
              }}
            >
              {saveState === "saving" && (
                <span
                  style={{
                    width: 15,
                    height: 15,
                    border: "2.5px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.65s linear infinite",
                  }}
                />
              )}
              {saveState === "saved" && (
                <span style={{ animation: "checkPop 300ms", display: "inline-block" }}>✓</span>
              )}
              {saveState === "saving" ? "Saving..." : saveState === "saved" ? "Saved!" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}